/**
 * @fileoverview Form validation schemas using Yup
 * @active Used across auth and settings forms
 * @see {@link src/components/auth/SignIn.tsx} - Sign in form
 * @see {@link src/components/auth/SignUp.tsx} - Sign up form
 * @see {@link src/types/userAuth.ts} - Auth type definitions
 */

import * as yup from "yup";
import type { SignInFormFields, SignUpFormFields } from "@/types/userAuth";

/**
 * Regular expressions for validation
 * @private
 */
const PATTERNS = {
  /** Validates names to only contain letters, spaces, hyphens, and apostrophes */
  NAME: /^[a-zA-Z\s\-']+$/,
  /** Validates URLs */
  URL: new RegExp(
    `(http(s)?://)?(www.)?[a-zA-Z0-9@:%._+~#=]{2,256}.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_+.~#?&/=]*)`
  ),
  /** Validates phone numbers */
  PHONE: /^[0-9+\-\s]*$/,
  /** Validates password special characters */
  PASSWORD_SPECIAL: /[!@#$%^&*()+\-=.{}[\]|\\:;"'<>,.?/~`]/,
} as const;

/**
 * Validation schemas for auth forms
 */
export const authValidation = {
  schemas: {
    signIn: yup.object<SignInFormFields>({
      email: yup
        .string()
        .required("Email is required")
        .email("Please enter a valid email"),
      password: yup.string().required("Password is required"),
    }),
    signUp: yup.object<SignUpFormFields>({
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
      email: yup
        .string()
        .required("Email is required")
        .email("Please enter a valid email"),
      password: yup
        .string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters"),
      confirmPassword: yup
        .string()
        .required("Please confirm your password")
        .oneOf([yup.ref("password")], "Passwords must match"),
    }),
  },
};

/**
 * Sign up form validation schema
 * @see {@link src/components/auth/SignUp.tsx}
 */
export const signUpSchema = yup.object({
  userFirstName: yup
    .string()
    .trim()
    .required("*Enter First Name")
    .matches(
      PATTERNS.NAME,
      "*The name can only contain letters, spaces, hyphens, and apostrophes"
    ),

  userLastName: yup
    .string()
    .trim()
    .required("*Enter Last Name")
    .matches(
      PATTERNS.NAME,
      "*The surname can only contain letters, spaces, hyphens, and apostrophes"
    ),
  email: yup.string().trim().email("*Incorrect email").required("*Enter email"),
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain an uppercase letter")
    .matches(/[0-9]/, "Password must contain a number")
    .matches(
      PATTERNS.PASSWORD_SPECIAL,
      "Password must contain a special character"
    )
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm Password is required"),
});

/**
 * Sign in form validation schema
 * @see {@link src/components/auth/SignIn.tsx}
 */
export const signInSchema = yup.object({
  email: yup.string().trim().email("*Incorrect email").required("*Enter email"),
  password: yup
    .string()
    .required("*Incorrect password")
    .min(8, "*min 8 char")
    .max(35, "*max 35 char"),
});

/**
 * Password creation/reset validation schema
 * @see {@link src/components/auth/Password.tsx}
 */
export const passwordSchema = yup.object({
  password: yup
    .string()
    .required("*Enter password")
    .min(8, "*min 8 char")
    .max(35, "*max 35 char")
    .matches(/^(?=.*[A-Z]).*$/, "*should contain at least 1 capital letter")
    .matches(/^(?=.*[0-9]).*$/, "*should contain at least 1 number")
    .matches(
      /^(?=.*[!@#$%^&*()+\-=]).*$/,
      "*should contain symbol  ! @ # $ % ^ & * ( ) +  "
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "*Passwords don't match each other")
    .required("*Confirm password is required"),
});

/**
 * Email validation schema
 * @see {@link src/components/auth/ForgotPassword.tsx}
 */
export const emailSchema = yup.object({
  email: yup.string().trim().email("*Incorrect email").required("*Enter email"),
});

/**
 * Profile update validation schema with password
 * @see {@link src/components/settings/account/profile/index.tsx}
 */
export const firstLastPasswordSchema = yup.object({
  userFirstName: yup.string().trim().required("*Enter First Name"),
  userLastName: yup.string().trim().required("*Enter Last Name"),
  password: yup.string().min(8, "*min 8 char").max(35, "*max 35 char"),
});

/**
 * Password change validation schema
 * @see {@link src/components/settings/account/security/index.tsx}
 */
export const changePasswordSchema = yup.object({
  password: yup
    .string()
    .required("*Enter password")
    .min(8, "*min 8 char")
    .max(35, "*max 35 char"),
  newPassword: yup
    .string()
    .required("*Enter new password")
    .min(8, "*min 8 char")
    .max(35, "*max 35 char")
    .matches(/^(?=.*[A-Z]).*$/, "*Should contain at least 1 capital letter")
    .matches(/^(?=.*[0-9]).*$/, "*Should contain at least 1 number")
    .matches(
      /^(?=.*[!@#$%^&*()+\-=]).*$/,
      "*Should contain symbol  ! @ # $ % ^ & * ( ) +  "
    ),
  confirmNewPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "*Passwords don't match each other")
    .required("*Confirm new password is required"),
});

/**
 * Social media links validation schema
 * @see {@link src/components/settings/account/profile/social/index.tsx}
 */
export const socialSchema = yup.object({
  linkedin: yup
    .string()
    .transform((value) => (value === "" ? undefined : value))
    .optional()
    .matches(PATTERNS.URL, "Enter correct linkedin link!"),

  twitter: yup
    .string()
    .transform((value) => (value === "" ? undefined : value))
    .optional()
    .matches(PATTERNS.URL, "Enter correct X link!"),

  website: yup
    .string()
    .transform((value) => (value === "" ? undefined : value))
    .optional()
    .matches(PATTERNS.URL, "Enter correct url!"),
});

/**
 * User profile information validation schema
 * @see {@link src/components/settings/account/profile/userInfo/index.tsx}
 */
export const infoProfileSchema = yup.object().shape({
  // Removed nickname validation as the field is no longer used
  // nickname: yup.string().trim().required("Nickname is required"),
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  email: yup
    .string()
    .trim()
    .email("Invalid email format")
    .required("Email is required"),
  // Removed phone validation as the field is no longer used
  // phone: yup
  //   .string()
  //   .transform((value) => (value === "" ? undefined : value))
  //   .optional()
  //   .matches(PATTERNS.PHONE, "Invalid phone number format"),
});

/**
 * Contact form validation schema
 * @see {@link src/components/contact/ContactForm.tsx}
 */
export const contactUsSchema = yup.object({
  firstName: yup.string().trim().required("*Enter First Name"),
  lastName: yup.string().trim().required("*Enter Last Name"),
  email: yup.string().trim().email("*Incorrect email").required("*Enter email"),
  companyName: yup.string().trim().required("*Enter Company Name"),
  phoneNumber: yup
    .string()
    .matches(PATTERNS.PHONE, "Enter correct phone number!")
    .required("Please enter phone number"),
  question: yup.string().trim().required("*Enter Question"),
  request: yup.string().trim().required("*Enter Details"),
});
